import mapboxgl from 'mapbox-gl';

/**
 * Vuex module that manages reusable Mapbox GL map instances.
 *
 * Instead of creating a new mapboxgl.Map every time a component mounts
 * (which counts as a billable "map load"), this module caches instances
 * by a string key and moves them between DOM containers on reuse.
 *
 * Usage from a component:
 *   const { map, isReused } = store.dispatch('mapbox/getOrCreateMap', {
 *     key: 'heatmap',
 *     container: mapContainer.value,
 *     options: { style, center, zoom, ... },
 *   });
 *
 *   // On unmount:
 *   store.dispatch('mapbox/releaseMap', 'heatmap');
 */

let parkingContainer = null;

function getParkingContainer() {
  if (!parkingContainer) {
    parkingContainer = document.createElement('div');
    parkingContainer.id = 'mapbox-parking-lot';
    parkingContainer.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none;';
    document.body.appendChild(parkingContainer);
  }
  return parkingContainer;
}

const state = () => ({
  // Map of key -> { instance, options } — kept as a plain object outside
  // reactive state to avoid Vue proxying the mapboxgl.Map instances.
});

// We store map instances outside Vue reactivity to prevent performance
// issues from Vue trying to make the complex mapbox objects reactive.
const mapInstances = {};

const getters = {
  hasMap: () => (key) => !!mapInstances[key],
};

const actions = {
  /**
   * Get an existing cached map or create a new one.
   *
   * @param {string} payload.key       Unique key, e.g. 'heatmap', 'exchange-map', 'load-details'
   * @param {HTMLElement} payload.container  DOM element to mount the map into
   * @param {object} payload.options   mapboxgl.Map options (style, center, zoom, etc.)
   *                                    `container` in options is ignored — use payload.container.
   * @returns {{ map: mapboxgl.Map, isReused: boolean }}
   */
  getOrCreateMap(_, { key, container, options = {} }) {
    if (!container) {
      console.error(`[mapbox store] No container provided for key "${key}"`);
      return { map: null, isReused: false };
    }

    const existing = mapInstances[key];

    if (existing) {
      // Move the existing map's container element into the new parent
      const mapContainer = existing.getContainer();
      container.appendChild(mapContainer);

      // Resize to fit the new container dimensions
      existing.resize();

      // Clear all custom layers / sources so the component can set up fresh
      clearAllCustomLayers(existing);

      // Remove all custom event listeners so they don't fire for old data
      clearAllCustomListeners(existing);

      return { map: existing, isReused: true };
    }

    // Set the access token if not already set.
    // Components typically set mapboxgl.accessToken before calling this,
    // so this is only a safety fallback.
    if (!mapboxgl.accessToken) {
      mapboxgl.accessToken = process.env.MAPBOX_TOKEN;
    }

    // Create fresh map
    const map = new mapboxgl.Map({
      ...options,
      container,
    });

    mapInstances[key] = map;

    return { map, isReused: false };
  },

  /**
   * Park the map offscreen instead of destroying it.
   * Call this from onBeforeUnmount.
   *
   * @param {string} key
   */
  releaseMap(_, key) {
    const map = mapInstances[key];
    if (!map) return;

    try {
      const mapContainer = map.getContainer();
      if (mapContainer) {
        getParkingContainer().appendChild(mapContainer);
      }
    } catch {
      // Map may have been removed externally; clean up our reference
      delete mapInstances[key];
    }
  },

  /**
   * Permanently destroy a map instance. Use this for full cleanup
   * (e.g., on logout or when a map is definitely no longer needed).
   *
   * @param {string} key
   */
  destroyMap(_, key) {
    const map = mapInstances[key];
    if (!map) return;

    try {
      map.remove();
    } catch {
      // already removed
    }
    delete mapInstances[key];
  },

  /**
   * Destroy all cached map instances.
   */
  destroyAll() {
    Object.keys(mapInstances).forEach((key) => {
      try {
        mapInstances[key].remove();
      } catch {
        // already removed
      }
      delete mapInstances[key];
    });
  },
};

const mutations = {};

/**
 * Remove all non-base-style layers and sources from the map.
 * This keeps the map instance alive but clears it for reuse.
 */
function clearAllCustomLayers(map) {
  try {
    const style = map.getStyle();
    if (!style) return;

    // Base source IDs that belong to the style itself (should not be removed)
    const baseSources = new Set(['composite', 'mapbox']);

    // Remove custom layers (iterate in reverse to avoid index issues)
    const layers = style.layers || [];
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      // Keep layers that are part of the base map style
      if (layer.source && !baseSources.has(layer.source)) {
        try {
          map.removeLayer(layer.id);
        } catch {
          // layer may already be removed
        }
      }
    }

    // Remove custom sources
    const sources = style.sources || {};
    Object.keys(sources).forEach((sourceId) => {
      if (!baseSources.has(sourceId)) {
        try {
          map.removeSource(sourceId);
        } catch {
          // source may already be removed
        }
      }
    });

    // Remove all custom images (icons loaded by components)
    // Keep mapbox built-in images that start with specific prefixes
    if (map.listImages) {
      const images = map.listImages();
      images.forEach((imageId) => {
        // Mapbox built-in images typically don't have simple names
        // Our custom images: 'origin', 'destination', 'hq', 'pulsing-dot', etc.
        if (!imageId.startsWith('mapbox-') && !imageId.includes(':')) {
          try {
            map.removeImage(imageId);
          } catch {
            // image may already be removed
          }
        }
      });
    }
  } catch (e) {
    console.warn('[mapbox store] Error clearing custom layers:', e);
  }
}

/**
 * Remove all custom event listeners from the map.
 * Mapbox GL JS stores listeners internally; we use the undocumented
 * _listeners property if available, otherwise we accept that some
 * listeners may persist (they should be harmless since sources are gone).
 */
function clearAllCustomListeners(map) {
  try {
    // Remove popups
    const popups = document.querySelectorAll('.mapboxgl-popup');
    popups.forEach((popup) => popup.remove());

    // Remove custom controls — we track these by class name
    const customControls = map
      .getContainer()
      .querySelectorAll(
        '.toggle-container, .legend-control, .debug-legend-control'
      );
    customControls.forEach((el) => el.remove());
  } catch (e) {
    console.warn('[mapbox store] Error clearing listeners:', e);
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
